import { useRef, useState } from 'react';

const TRIGGER_PX = 70;
const MAX_PULL_PX = 110;

/**
 * Pull-to-refresh for the installed PWA (standalone mode has no browser
 * refresh). Touch-only; desktop is unaffected.
 *
 *   const ptr = usePullToRefresh(async () => { await refetchAll(); });
 *   <div {...ptr.containerProps}>
 *     {ptr.indicatorVisible ? <PullIndicator progress={ptr.progress} refreshing={ptr.refreshing} /> : null}
 *     …page content…
 *   </div>
 *
 * The pull only arms when the window is scrolled to the very top.
 */
export const usePullToRefresh = (onRefresh: () => Promise<unknown>) => {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);

  const onTouchStart = (event: React.TouchEvent) => {
    if (window.scrollY > 0 || refreshing) {
      startY.current = null;
      return;
    }
    startY.current = event.touches[0].clientY;
  };

  const onTouchCancel = () => {
    startY.current = null;
    setPull(0);
  };

  const onTouchMove = (event: React.TouchEvent) => {
    if (startY.current === null || refreshing) {
      return;
    }
    const delta = event.touches[0].clientY - startY.current;
    if (delta <= 0 || window.scrollY > 0) {
      setPull(0);
      return;
    }
    // Diminishing resistance as the pull grows.
    setPull(Math.min(MAX_PULL_PX, delta * 0.5));
  };

  const onTouchEnd = () => {
    if (startY.current === null) {
      return;
    }
    startY.current = null;
    if (pull >= TRIGGER_PX && !refreshing) {
      setRefreshing(true);
      void onRefresh().finally(() => {
        setRefreshing(false);
        setPull(0);
      });
    } else {
      setPull(0);
    }
  };

  return {
    containerProps: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
    /** 0..1 — how close the pull is to triggering */
    progress: Math.min(1, pull / TRIGGER_PX),
    pull,
    refreshing,
    indicatorVisible: pull > 4 || refreshing,
  };
};
